import { Building2, Bell, Wrench } from 'lucide-react'
import { LANDING_PAGE_COMMAND_CENTER } from '@propbix/constants/public'

const toneClass = {
  ok: 'bg-emerald-400/15 text-emerald-300',
  warn: 'bg-amber-400/15 text-amber-200',
  info: 'bg-teal-400/15 text-teal-200',
  active: 'bg-teal-400/20 text-teal-200',
  quiet: 'bg-white/5 text-slate-400',
} as const

const floatToneClass = {
  info: 'border-teal-400/30 bg-[rgb(var(--desk-panel))]/95 text-teal-100',
  warn: 'border-amber-400/30 bg-[rgb(var(--desk-panel))]/95 text-amber-100',
} as const

type OwnerCommandCenterProps = {
  showFloatCards?: boolean
  /** Hero uses a tighter composition that fits the landing column without hard clipping. */
  variant?: 'hero' | 'full'
}

export function OwnerCommandCenter({
  showFloatCards = false,
  variant = 'full',
}: OwnerCommandCenterProps) {
  const cc = LANDING_PAGE_COMMAND_CENTER
  const isHero = variant === 'hero'

  return (
    <div className={`relative mx-auto w-full ${isHero ? 'max-w-none' : 'max-w-[1180px]'}`}>
      <div
        className="pointer-events-none absolute -inset-x-6 -bottom-8 top-1/4 rounded-[40%] bg-[radial-gradient(ellipse_at_center,rgb(var(--accent-glow)/0.2),transparent_65%)] blur-2xl"
        aria-hidden
      />

      {showFloatCards && !isHero ? (
        <div className="pointer-events-none absolute inset-0 z-[2] hidden lg:block" aria-hidden>
          {cc.floatCards.map((card, index) => (
            <div
              key={card.id}
              className={`absolute w-[220px] rounded-[var(--radius-card)] border p-3.5 shadow-[var(--shadow-float)] ${floatToneClass[card.tone]} ${
                index === 0 ? 'float-y -left-2 top-[12%] xl:-left-6' : 'float-y-delayed -right-2 bottom-[18%] xl:-right-8'
              }`}
            >
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-slate-400">
                {card.title}
              </p>
              <p className="mt-1.5 text-sm font-medium leading-snug text-white">{card.body}</p>
              <p className="mt-2 text-xs font-semibold text-teal-300">{card.action}</p>
            </div>
          ))}
        </div>
      ) : null}

      <div className="command-desk relative overflow-hidden ring-1 ring-white/10">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-teal-300/40 to-transparent"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -right-20 -top-24 size-64 rounded-full bg-teal-400/10 blur-3xl"
          aria-hidden
        />

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3.5 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-teal-500/20 text-teal-300">
              <Building2 size={17} strokeWidth={1.75} />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold tracking-tight text-white">{cc.deskTitle}</p>
              <p className="truncate text-xs text-slate-400">{cc.deskSubtitle}</p>
            </div>
          </div>
          <span className="desk-badge border border-white/10 bg-white/5 text-slate-300">{cc.exampleLabel}</span>
        </div>

        {isHero && showFloatCards ? (
          <div className="grid gap-2 border-b border-white/10 bg-white/[0.03] px-4 py-3 sm:grid-cols-2 sm:px-5">
            {cc.floatCards.map((card) => (
              <div
                key={card.id}
                className={`rounded-[var(--radius-card)] border px-3 py-2.5 ${floatToneClass[card.tone]}`}
              >
                <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-slate-400">
                  {card.title}
                </p>
                <p className="mt-1 text-sm font-medium leading-snug text-white">{card.body}</p>
                <p className="mt-1.5 text-xs font-semibold text-teal-300">{card.action}</p>
              </div>
            ))}
          </div>
        ) : null}

        {isHero ? (
          <div className="grid gap-3 p-4 sm:gap-4 sm:p-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
            <div className="min-w-0 space-y-3 sm:space-y-4">
              <div className="grid grid-cols-2 gap-2.5">
                {cc.metrics.map((metric) => (
                  <div key={metric.label} className="command-widget px-3 py-3">
                    <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-slate-500">
                      {metric.label}
                    </p>
                    <p className="mt-1.5 font-display text-xl font-semibold tracking-tight text-white">
                      {metric.value}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-400">{metric.detail}</p>
                  </div>
                ))}
              </div>

              <div className="command-widget p-3.5">
                <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  {cc.attentionTitle}
                </p>
                <ul className="mt-3 space-y-2">
                  {cc.attention.slice(0, 3).map((row) => (
                    <li
                      key={row.label}
                      className="flex items-center justify-between gap-2 rounded-lg bg-white/[0.03] px-3 py-2.5"
                    >
                      <span className="truncate text-sm font-medium text-slate-100">{row.label}</span>
                      <span className={`desk-badge shrink-0 ${toneClass[row.tone]}`}>{row.status}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="command-widget p-3.5">
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-slate-500">
                      {cc.rentTitle}
                    </p>
                    <p className="mt-1 text-sm text-slate-400">{cc.rentCaption}</p>
                  </div>
                  <p className="font-display text-2xl font-semibold text-teal-300">{cc.rentCollectedPct}%</p>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-teal-600 to-teal-300"
                    style={{ width: `${cc.rentCollectedPct}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="min-w-0 space-y-3 sm:space-y-4">
              <div className="command-widget p-3.5">
                <div className="flex items-center gap-2">
                  <Wrench size={14} className="text-teal-300" strokeWidth={1.75} />
                  <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-slate-500">
                    {cc.boardTitle}
                  </p>
                </div>
                <ul className="mt-3 space-y-2">
                  {cc.board.map((job) => (
                    <li key={job.code} className="rounded-lg bg-white/[0.03] px-3 py-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-teal-300/90">{job.code}</span>
                        <span className="desk-badge bg-white/5 text-slate-400">{job.column}</span>
                      </div>
                      <p className="mt-1 text-sm text-slate-200">{job.title}</p>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="command-widget p-3.5">
                <div className="flex items-center gap-2">
                  <Bell size={14} className="text-teal-300" strokeWidth={1.75} />
                  <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-slate-500">
                    {cc.inboxTitle}
                  </p>
                </div>
                <ul className="mt-3 space-y-2.5">
                  {cc.inbox.slice(0, 2).map((item) => (
                    <li key={item.title} className="rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium leading-snug text-slate-100">{item.title}</p>
                        <span className="shrink-0 text-[0.65rem] text-slate-500">{item.time}</span>
                      </div>
                      <p className="mt-1 text-xs text-slate-400">{item.meta}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 p-4 lg:grid-cols-[200px_minmax(0,1fr)_240px] lg:gap-5 lg:p-5">
            <aside className="command-widget hidden p-3 lg:block">
              <p className="px-2 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-slate-500">
                {cc.buildingSwitcher}
              </p>
              <ul className="mt-2 space-y-1">
                {cc.buildings.map((building) => (
                  <li
                    key={building.name}
                    className="flex items-center justify-between rounded-lg px-2.5 py-2 text-sm hover:bg-white/5"
                  >
                    <span className="font-medium text-slate-100">{building.name}</span>
                    <span className={`desk-badge ${toneClass[building.tone]}`}>{building.units}</span>
                  </li>
                ))}
              </ul>
            </aside>

            <div className="min-w-0 space-y-4">
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                {cc.metrics.map((metric) => (
                  <div key={metric.label} className="command-widget px-3 py-3">
                    <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-slate-500">
                      {metric.label}
                    </p>
                    <p className="mt-1.5 font-display text-xl font-semibold tracking-tight text-white sm:text-2xl">
                      {metric.value}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-400">{metric.detail}</p>
                  </div>
                ))}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="command-widget p-3.5">
                  <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-slate-500">
                    {cc.attentionTitle}
                  </p>
                  <ul className="mt-3 space-y-2">
                    {cc.attention.map((row) => (
                      <li
                        key={row.label}
                        className="flex items-center justify-between gap-2 rounded-lg bg-white/[0.03] px-3 py-2.5"
                      >
                        <span className="text-sm font-medium text-slate-100">{row.label}</span>
                        <span className={`desk-badge shrink-0 ${toneClass[row.tone]}`}>{row.status}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="command-widget p-3.5">
                  <div className="flex items-center gap-2">
                    <Wrench size={14} className="text-teal-300" strokeWidth={1.75} />
                    <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-slate-500">
                      {cc.boardTitle}
                    </p>
                  </div>
                  <ul className="mt-3 space-y-2">
                    {cc.board.map((job) => (
                      <li key={job.code} className="rounded-lg bg-white/[0.03] px-3 py-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-semibold text-teal-300/90">{job.code}</span>
                          <span className="desk-badge bg-white/5 text-slate-400">{job.column}</span>
                        </div>
                        <p className="mt-1 text-sm text-slate-200">{job.title}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="command-widget p-3.5">
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-slate-500">
                      {cc.rentTitle}
                    </p>
                    <p className="mt-1 text-sm text-slate-400">{cc.rentCaption}</p>
                  </div>
                  <p className="font-display text-2xl font-semibold text-teal-300">{cc.rentCollectedPct}%</p>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-teal-600 to-teal-300"
                    style={{ width: `${cc.rentCollectedPct}%` }}
                  />
                </div>
              </div>
            </div>

            <aside className="relative space-y-4">
              <div className="command-widget float-y p-3.5">
                <div className="flex items-center gap-2">
                  <Bell size={14} className="text-teal-300" strokeWidth={1.75} />
                  <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-slate-500">
                    {cc.inboxTitle}
                  </p>
                </div>
                <ul className="mt-3 space-y-2.5">
                  {cc.inbox.map((item) => (
                    <li key={item.title} className="rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium leading-snug text-slate-100">{item.title}</p>
                        <span className="shrink-0 text-[0.65rem] text-slate-500">{item.time}</span>
                      </div>
                      <p className="mt-1 text-xs text-slate-400">{item.meta}</p>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="command-widget float-y-delayed hidden p-3.5 lg:block">
                <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  {cc.buildingsChartTitle}
                </p>
                <div className="mt-3 flex gap-2">
                  {cc.buildings.map((b) => (
                    <div
                      key={b.name}
                      className="flex h-16 flex-1 flex-col justify-end rounded-lg bg-gradient-to-t from-teal-500/25 to-white/5 px-2 pb-2"
                    >
                      <span className="truncate text-[0.65rem] text-slate-400">{b.name.split(' ')[0]}</span>
                      <span className="text-sm font-semibold text-white">{b.units}</span>
                    </div>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  )
}
